import {
  FILTER_NAME_TO_KPI_FILTER,
  extractFilterKeyFromNodeId,
  type AllFilterType,
  type ProductTaxonomyType
} from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type Ref } from 'vue';

import { type FiltersTreeType } from '#composables/kpisPerimeter';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';
import { searchFilters } from '#domain/core/filter/filter.query';

interface UseNomenclatureDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useNomenclatureData({
  debouncedSearch,
  checkedItemsState,
  enabled,
  taxonomy
}: UseNomenclatureDataProps) {
  const {
    data: nomenclatureData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [searchFilters.name, debouncedSearch, taxonomy],
    queryFn: async () => {
      const query: { [key in AllFilterType]?: string[] } = {};

      const currentNomenclatureFilters = Object.fromEntries(
        Object.entries(checkedItemsState.value.nomenclatureFilters).filter(([, state]) => state.state === 'checked')
      );

      Object.values(currentNomenclatureFilters).forEach(({ filterType, nodeId }) => {
        query[filterType as keyof typeof FILTER_NAME_TO_KPI_FILTER] = [
          ...(query[filterType as keyof typeof FILTER_NAME_TO_KPI_FILTER] || []),
          extractFilterKeyFromNodeId(nodeId)
        ];
      });

      return searchFilters({
        search: debouncedSearch.value,
        query,
        taxonomy: unref(taxonomy)
      });
    },
    select: (result) => {
      if (result.status === 'error') {
        return undefined;
      }

      return result.data;
    }
  });

  const itemsNomenclature = computed(() => {
    if (!nomenclatureData.value) {
      return undefined;
    }

    return filterToTreeViewItemWithDefaultValue({
      filter: nomenclatureData.value,
      defaultFilters: nomenclatureData.value,
      selectedFilters: checkedItemsState.value.nomenclatureFilters
    });
  });

  const isLoadingItemsNomenclature = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return { itemsNomenclature, isLoadingItemsNomenclature };
}
